import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useForm, sectionConfig } from '../../context/FormContext';
import Header from '../common/Header/Header';
import ApplicationStatusHeader from '../common/ApplicationStatusHeader/ApplicationStatusHeader';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import SectionNavigation from '../SectionNavigation/SectionNavigation';
import Introduction from '../sections/Introduction/Introduction';
import CompanyDetails from '../sections/CompanyDetails/CompanyDetails';
import ContactInformation from '../sections/ContactInformation/ContactInformation';
import PepInformation from '../sections/PepInformation/PepInformation';
import SanctionsAudit from '../sections/SanctionsAudit/SanctionsAudit';
import BusinessPresentation from '../sections/BusinessPresentation/BusinessPresentation';
import Documentation from '../sections/Documentation/Documentation';
import SubmissionConfirmation from '../SubmissionConfirmation/SubmissionConfirmation';
import apiService from '../../services/api';
import './OnboardingForm.css';

const OnboardingForm = () => {
  const { state, actions } = useForm();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Auth0 token getter is now initialized at the App level

  // Check if form is already submitted
  useEffect(() => {
    if (state.isSubmitted) {
      navigate('/success');
    }
  }, [state.isSubmitted, navigate]);

  // Section components mapping
  const sectionComponents = {
    introduction: Introduction,
    companyDetails: CompanyDetails,
    contactInformation: ContactInformation,
    pepInformation: PepInformation,
    sanctionsAudit: SanctionsAudit,
    businessPresentation: BusinessPresentation,
    documentation: Documentation
  };

  const currentSectionConfig = sectionConfig[state.currentSection];
  const CurrentSectionComponent = sectionComponents[currentSectionConfig.id];

  // Handle section navigation
  const handleSectionChange = (sectionIndex) => {
    // Allow navigation even in read-only mode so users can view all sections
    actions.setCurrentSection(sectionIndex);
    setIsSidebarOpen(false); // Close sidebar on mobile after selection
  };

  // Handle sidebar toggle
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Close sidebar when clicking outside
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Handle body scroll lock for mobile sidebar
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Handle section save
  const handleSectionSave = async (sectionId, data) => {
    if (state.isReadOnly) {
      console.warn('Cannot save section in read-only mode');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiService.saveSectionDraft(sectionId, data);

      if (response.success) {
        // Note: Section validation and status update is now handled in individual components
        // Show success message
        console.log('Section saved successfully');
      } else {
        actions.setSectionErrors(sectionId, response.errors || {});
      }
    } catch (error) {
      console.error('Error saving section:', error);
      // Handle error
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleFormSubmit = async () => {
    setIsLoading(true);

    try {
      const response = await apiService.submitForm(state);

      if (response.success) {
        actions.submitForm();
        navigate('/success');
      } else {
        // Handle submission errors
        Object.keys(response.errors || {}).forEach(sectionId => {
          actions.setSectionErrors(sectionId, response.errors[sectionId]);
        });
        setShowConfirmation(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setShowConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if all sections are completed (excluding introduction)
  const allSectionsCompleted = Object.entries(state.sections)
    .filter(([sectionId]) => sectionId !== 'introduction')
    .every(([, section]) => section.status === 'completed');

  // Handle submit button click
  const handleSubmitClick = () => {
    if (allSectionsCompleted) {
      setShowConfirmation(true);
    }
  };

  // Show loading spinner while loading application data
  if (state.isLoadingApplication) {
    return (
      <div className="onboarding-form">
        <Header
          onMenuToggle={toggleSidebar}
          showMobileMenu={true}
        />
        <div className="container">
          <LoadingSpinner
            message="Loading your application..."
            size="large"
          />
        </div>
      </div>
    );
  }

  if (state.isSubmitted) {
    return (
      <div className="onboarding-form">
        <div className="container">
          <div className="submitted-notice">
            <h2>Form Already Submitted</h2>
            <p>Your application has been submitted and cannot be modified.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/success')}
            >
              View Submission Details
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-form">
      <Header
        onMenuToggle={toggleSidebar}
        showMobileMenu={true}
      />

      <div className="container">
        {/* Main Content */}
        <div className="form-content">
          <div className="form-main">
            {/* Application Status Header - moved inside form */}
            <ApplicationStatusHeader
              applicationStatus={state.applicationStatus}
              referenceNumber={state.referenceNumber}
              submissionDate={state.submissionDate}
              isReadOnly={state.isReadOnly}
            />

            {/* Current Section */}
            <div className="section-container">
              <div className="section-header">
                <h2>{currentSectionConfig.title}</h2>
                <p>{currentSectionConfig.description}</p>
              </div>

              <div className="section-content">
                {CurrentSectionComponent && (
                  <CurrentSectionComponent
                    data={state.sections[currentSectionConfig.id].data}
                    errors={state.sections[currentSectionConfig.id].errors}
                    onSave={(data) => handleSectionSave(currentSectionConfig.id, data)}
                    isLoading={isLoading}
                    isReadOnly={state.isSubmitted || state.isReadOnly}
                  />
                )}
              </div>
            </div>

            {/* Navigation */}
            <SectionNavigation
              currentSection={state.currentSection}
              totalSections={sectionConfig.length}
              onPrevious={() => handleSectionChange(state.currentSection - 1)}
              onNext={() => handleSectionChange(state.currentSection + 1)}
              onSubmit={handleSubmitClick}
              canSubmit={allSectionsCompleted && !state.isReadOnly}
              isLoading={isLoading}
              isReadOnly={state.isReadOnly}
            />
          </div>

          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="sidebar-overlay overlay-visible"
              onClick={closeSidebar}
            ></div>
          )}

          {/* Sidebar */}
          <div className={`form-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
            <div className="sidebar-content">
              <div className="sidebar-header">
                <h3>Section Status</h3>
                <button
                  className="sidebar-close-btn"
                  onClick={closeSidebar}
                  aria-label="Close menu"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <div className="section-status-list">
                {sectionConfig.map((section, index) => {
                  const status = state.sections[section.id].status;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      className={`status-item ${status} ${index === state.currentSection ? 'current' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSectionChange(index);
                      }}
                    >
                      <div className="status-indicator">
                        {status === 'completed' && <span className="checkmark">✓</span>}
                        {status === 'in_progress' && <span className="progress-dot">●</span>}
                        {status === 'not_started' && <span className="empty-dot">○</span>}
                      </div>
                      <div className="status-text">
                        <div className="status-title">{section.title}</div>
                        <div className="status-label">
                          {status === 'completed' && 'Completed'}
                          {status === 'in_progress' && 'In Progress'}
                          {status === 'not_started' && 'Not Started'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {allSectionsCompleted && !state.isReadOnly && (
                <div className="submit-section">
                  <button
                    className="btn btn-success btn-lg w-full"
                    onClick={handleSubmitClick}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submission Confirmation Modal */}
        {showConfirmation && (
          <SubmissionConfirmation
            onConfirm={handleFormSubmit}
            onCancel={() => setShowConfirmation(false)}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default OnboardingForm;
